import {
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import {
  ActionIcon,
  MagnusOperaIcon,
  SidebarToggleIcon,
  ThemeToggle,
} from "@insights/ui";
import { authClient } from "../../api";
import { useWorkspaceBillingAlert } from "../hooks/useWorkspaceBillingAlert";
import { useWorkspaceContext } from "../hooks/useWorkspaceContext";
import { WorkspaceMenu } from "./WorkspaceMenu";
import {
  buildWorkspacePath,
  getActiveWorkspaceNavigation,
  getWorkspaceTopics,
} from "./workspaceNavigation";

export function AppShell({ children }: { children: ReactNode }) {
  const organizations = authClient.useListOrganizations();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const active = params.workspaceId ?? organizations.data?.[0]?.id;
  const workspace = useWorkspaceContext(params.workspaceId);
  const workspaceRole = workspace.data?.role;
  const workspaceFeatures = workspace.data?.features;
  const workspaceTopics = getWorkspaceTopics(workspaceRole, workspaceFeatures);
  const activeNavigation = getActiveWorkspaceNavigation(
    location.pathname,
    workspaceRole,
    workspaceFeatures,
  );
  const billingAlert = useWorkspaceBillingAlert(
    params.workspaceId,
    workspaceRole === "owner",
  );
  const sectionMarkerRef = useRef<HTMLSpanElement>(null);
  const mobileSectionMarkerRef = useRef<HTMLSpanElement>(null);
  const activeMobileSectionRef = useRef<HTMLAnchorElement>(null);
  const mobileSectionTabsRef = useRef<HTMLElement>(null);
  const sectionNavigationPendingRef = useRef(false);
  const [mobileSectionOverflow, setMobileSectionOverflow] = useState({
    left: false,
    right: false,
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("insights-sidebar-collapsed") === "true",
  );

  useLayoutEffect(() => {
    if (!window.matchMedia("(max-width: 850px)").matches) {
      return;
    }

    const activeSection = activeMobileSectionRef.current;
    const tabList = mobileSectionTabsRef.current;
    if (!activeSection || !tabList) {
      setMobileSectionOverflow({
        left: false,
        right: false,
      });
      return;
    }
    const tabs = tabList;

    const centeredPosition =
      activeSection.offsetLeft -
      (tabs.clientWidth - activeSection.clientWidth) / 2;
    tabs.scrollTo({
      behavior: "auto",
      left: Math.max(0, centeredPosition),
    });

    function updateOverflow() {
      const maximumScrollLeft = tabs.scrollWidth - tabs.clientWidth;
      setMobileSectionOverflow({
        left: tabs.scrollLeft > 2,
        right: tabs.scrollLeft < maximumScrollLeft - 2,
      });
    }

    updateOverflow();
    tabs.addEventListener("scroll", updateOverflow, {
      passive: true,
    });
    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(tabs);

    return () => {
      tabs.removeEventListener("scroll", updateOverflow);
      resizeObserver.disconnect();
    };
  }, [activeNavigation?.section.path, mobileMenuOpen]);
  const shellClass = `app-shell${mobileMenuOpen ? " mobile-menu-open" : ""}${sidebarCollapsed ? " sidebar-collapsed" : ""}`;

  function toggleSidebar() {
    setSidebarCollapsed((collapsed) => {
      localStorage.setItem("insights-sidebar-collapsed", String(!collapsed));
      return !collapsed;
    });
  }

  function navigateToSection(
    event: ReactMouseEvent<HTMLAnchorElement>,
    destination: string,
    isActive: boolean,
    marker: HTMLSpanElement | null,
  ) {
    if (
      isActive ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey
    ) {
      return;
    }

    event.preventDefault();

    const targetLabel = event.currentTarget.querySelector<HTMLElement>(
      "[data-section-label]",
    );
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (
      !marker ||
      !targetLabel ||
      reduceMotion ||
      sectionNavigationPendingRef.current
    ) {
      navigate(destination);
      return;
    }

    sectionNavigationPendingRef.current = true;
    const markerBounds = marker.getBoundingClientRect();
    const targetBounds = targetLabel.getBoundingClientRect();
    const horizontalDistance = targetBounds.left - markerBounds.left;
    const widthScale = targetBounds.width / markerBounds.width;
    const animation = marker.animate(
      [
        {
          transform: "translateX(0) scaleX(1)",
        },
        {
          transform: `translateX(${horizontalDistance}px) scaleX(${widthScale})`,
        },
      ],
      {
        duration: 240,
        easing: "cubic-bezier(.4, 0, .2, 1)",
        fill: "forwards",
      },
    );

    void animation.finished
      .catch(() => undefined)
      .then(() => navigate(destination));
  }

  return (
    <div className={shellClass}>
      <aside className="app-sidebar">
        <div className="aside-header">
          <Link to="/" className="brand" aria-label="Insights home">
            <MagnusOperaIcon />
            <span className="brand-name">Insights</span>
          </Link>
          <div className="mobile-header-actions">
            {activeNavigation && (
              <span
                aria-hidden={mobileMenuOpen || undefined}
                className="mobile-topic-label"
              >
                {activeNavigation.topic.label}
              </span>
            )}
            <ActionIcon
              className="mobile-menu-toggle"
              icon={mobileMenuOpen ? <X /> : <Menu />}
              label={
                mobileMenuOpen ? "Close navigation" : "Open navigation"
              }
              aria-expanded={mobileMenuOpen}
              aria-controls="primary-navigation"
              onClick={() => setMobileMenuOpen((open) => !open)}
            />
          </div>
        </div>
        {active && activeNavigation && (
          <div className="mobile-context-navigation">
            {mobileMenuOpen ? (
              <WorkspaceMenu
                workspaces={organizations.data ?? []}
                active={active}
              />
            ) : (
              <nav
                aria-label={`${activeNavigation.topic.label} sections`}
                className="mobile-section-tabs"
                ref={mobileSectionTabsRef}
              >
                {activeNavigation.topic.sections.map((section) => {
                  const destination = buildWorkspacePath(section.path, active);
                  const isActive = section === activeNavigation.section;
                  const SectionIcon = section.icon;

                  return (
                    <Link
                      aria-current={isActive ? "page" : undefined}
                      key={section.label}
                      onClick={(event) =>
                        navigateToSection(
                          event,
                          destination,
                          isActive,
                          mobileSectionMarkerRef.current,
                        )
                      }
                      ref={isActive ? activeMobileSectionRef : undefined}
                      to={destination}
                    >
                      <SectionIcon aria-hidden="true" />
                      <span data-section-label>
                        {section.label}
                        {isActive && (
                          <span
                            aria-hidden="true"
                            className="section-navigation-marker mobile-section-marker"
                            ref={mobileSectionMarkerRef}
                          />
                        )}
                      </span>
                    </Link>
                  );
                })}
              </nav>
            )}
            {!mobileMenuOpen && mobileSectionOverflow.left && (
              <span
                aria-hidden="true"
                className="mobile-section-swipe-hint mobile-section-swipe-hint--left"
              >
                <ChevronLeft />
              </span>
            )}
            {!mobileMenuOpen && mobileSectionOverflow.right && (
              <span
                aria-hidden="true"
                className="mobile-section-swipe-hint mobile-section-swipe-hint--right"
              >
                <ChevronRight />
              </span>
            )}
          </div>
        )}
        <div className="mobile-navigation-panel">
          <div className="workspace-label">Workspace</div>
          <WorkspaceMenu
            workspaces={organizations.data ?? []}
            active={active}
          />
          {active && (
            <nav
              aria-label="Workspace topics"
              className="topic-navigation"
              id="primary-navigation"
              onClick={() => setMobileMenuOpen(false)}
            >
              {workspaceTopics.map((topic) => {
                const TopicIcon = topic.icon;
                const destination = buildWorkspacePath(
                  topic.sections[0].path,
                  active,
                );
                const isActive = topic.sections.some(
                  (section) =>
                    section.path === activeNavigation?.section.path,
                );

                return (
                  <Link
                    aria-current={isActive ? "page" : undefined}
                    key={topic.label}
                    title={topic.label}
                    to={destination}
                  >
                    <TopicIcon />
                    <span className="topic-navigation-label">
                      {topic.label}
                    </span>
                  </Link>
                );
              })}
            </nav>
          )}
          <div className="aside-footer">
            <ActionIcon
              className="sign-out"
              color="danger"
              icon={<LogOut />}
              label="Sign out"
              onClick={() => authClient.signOut()}
            />
            <ThemeToggle />
            <ActionIcon
              className="desktop-sidebar-toggle"
              icon={<SidebarToggleIcon collapsed={sidebarCollapsed} />}
              label={
                sidebarCollapsed ? "Expand navigation" : "Collapse navigation"
              }
              aria-expanded={!sidebarCollapsed}
              onClick={toggleSidebar}
            />
          </div>
        </div>
      </aside>
      <section className="page">
        {active && activeNavigation && (
          <div className="section-navigation">
            <nav
              aria-label={`${activeNavigation.topic.label} sections`}
              className="section-navigation-links"
            >
              {activeNavigation.topic.sections.map((section) => {
                const destination = buildWorkspacePath(section.path, active);
                const isActive = section === activeNavigation.section;
                const SectionIcon = section.icon;

                return (
                  <Link
                    aria-current={isActive ? "page" : undefined}
                    key={section.label}
                    onClick={(event) =>
                      navigateToSection(
                        event,
                        destination,
                        isActive,
                        sectionMarkerRef.current,
                      )
                    }
                    to={destination}
                  >
                    <SectionIcon aria-hidden="true" />
                    <span data-section-label>
                      {section.label}
                      {isActive && (
                        <span
                          aria-hidden="true"
                          className="section-navigation-marker"
                          ref={sectionMarkerRef}
                        />
                      )}
                    </span>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
        <div className="page-content">
          {billingAlert && params.workspaceId && (
            <div
              className={`workspace-billing-alert workspace-billing-alert--${billingAlert.tone}`}
              role="alert"
            >
              <span>{billingAlert.message}</span>
              {billingAlert.action && (
                <Link to={`/w/${params.workspaceId}/billing`}>
                  {billingAlert.action}
                </Link>
              )}
            </div>
          )}
          {children}
        </div>
      </section>
    </div>
  );
}
